class TLJRingSizeConverter extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.input = this.querySelector('[data-size-input]');
    this.inputLabel = this.querySelector('[data-input-label]');
    this.inputHint = this.querySelector('[data-input-hint]');
    this.inputError = this.querySelector('[data-input-error]');
    this.clearButton = this.querySelector('[data-clear-input]');
    this.suggestions = this.querySelector('[data-suggestions]');
    this.sourceInputs = [...this.querySelectorAll('input[name^="ring-size-source-"]')];
    this.resultLabel = this.querySelector('[data-result-label]');
    this.resultEmpty = this.querySelector('[data-result-empty]');
    this.resultValues = this.querySelector('[data-result-values]');
    this.resultUK = this.querySelector('[data-result-uk]');
    this.resultUS = this.querySelector('[data-result-us]');
    this.resultDiameter = this.querySelector('[data-result-diameter]');
    this.resultCircumference = this.querySelector('[data-result-circumference]');
    this.resultStatus = this.querySelector('[data-result-status]');
    this.preview = this.querySelector('[data-ring-preview]');
    this.previewSize = this.querySelector('[data-preview-size]');
    this.tableRows = [...this.querySelectorAll('[data-size-row]')];
    this.rows = this.tableRows.map((element, index) => ({
      element,
      index,
      uk: element.dataset.uk.trim(),
      us: element.dataset.us.trim(),
      diameter: element.dataset.diameter.trim(),
      circumference: element.dataset.circumference.trim(),
    }));
    this.activeOptionIndex = -1;
    this.currentSuggestions = [];

    if (!this.input || !this.suggestions || this.rows.length === 0) return;

    this.bindEvents();
    this.applySource('uk');
    this.resetResult();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.initialized = false;
  }

  bindEvents() {
    const signal = this.abortController.signal;

    this.input.addEventListener('input', () => this.handleInput(), { signal });
    this.input.addEventListener('focus', () => this.handleInput(), { signal });
    this.input.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.input.addEventListener(
      'blur',
      () => {
        window.setTimeout(() => {
          if (document.activeElement !== this.input) this.closeSuggestions();
        }, 0);
      },
      { signal }
    );

    this.sourceInputs.forEach((sourceInput) => {
      sourceInput.addEventListener(
        'change',
        () => {
          if (sourceInput.checked) this.applySource(sourceInput.value);
        },
        { signal }
      );
    });

    this.clearButton.addEventListener(
      'click',
      () => {
        this.input.value = '';
        this.handleInput();
        this.input.focus();
      },
      { signal }
    );

    this.suggestions.addEventListener(
      'pointerdown',
      (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;
        event.preventDefault();
        this.chooseSuggestion(Number(option.dataset.suggestionIndex));
      },
      { signal }
    );

    this.suggestions.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option || !this.suggestions.contains(option)) return;
        this.chooseSuggestion(Number(option.dataset.suggestionIndex));
      },
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.contains(event.target)) this.closeSuggestions();
      },
      { signal }
    );
  }

  applySource(source) {
    this.source = source === 'us' ? 'us' : 'uk';
    const isUS = this.source === 'us';

    this.inputLabel.textContent = isUS ? this.dataset.usInputLabel : this.dataset.ukInputLabel;
    this.input.placeholder = isUS ? this.dataset.usPlaceholder : this.dataset.ukPlaceholder;
    this.inputHint.textContent = isUS ? this.dataset.usHint : this.dataset.ukHint;
    this.input.inputMode = isUS ? 'decimal' : 'text';
    this.input.autocapitalize = isUS ? 'off' : 'characters';
    this.input.value = '';
    this.clearError();
    this.closeSuggestions();
    this.clearButton.hidden = true;
    this.resetResult();
  }

  handleInput() {
    const rawValue = this.input.value;
    const value = this.source === 'uk' ? this.normalizeUK(rawValue) : this.normalizeUS(rawValue);

    this.clearButton.hidden = rawValue.length === 0;
    this.clearError();

    if (!value) {
      this.closeSuggestions();
      this.resetResult();
      return;
    }

    const exactMatch = this.findExactMatch(value);
    if (exactMatch) {
      this.closeSuggestions();
      this.updateResult(exactMatch, true, true);
      return;
    }

    const suggestions = this.findSuggestions(value);
    if (suggestions.length === 0) {
      this.closeSuggestions();
      this.showError();
      this.resetResult();
      return;
    }

    this.updateResult(suggestions[0], false, false);
    this.renderSuggestions(suggestions);
  }

  normalizeUK(value) {
    return value
      .trim()
      .toUpperCase()
      .replace(/½/g, ' 1/2')
      .replace(/¼/g, ' 1/4')
      .replace(/¾/g, ' 3/4')
      .replace(/^([A-Z])(?=\d)/, '$1 ')
      .replace(/\s+/g, ' ');
  }

  normalizeUS(value) {
    return value.trim().replace(',', '.');
  }

  findExactMatch(value) {
    if (this.source === 'uk') {
      return this.rows.find((row) => this.normalizeUK(row.uk) === value);
    }

    const numericValue = Number.parseFloat(value);
    if (!Number.isFinite(numericValue)) return undefined;
    return this.rows.find((row) => Math.abs(Number.parseFloat(row.us) - numericValue) < 0.0001);
  }

  findSuggestions(value) {
    if (this.source === 'uk') {
      return this.rows
        .map((row) => {
          const candidate = this.normalizeUK(row.uk);
          let score = Number.POSITIVE_INFINITY;

          if (candidate.startsWith(value)) score = candidate.length - value.length;
          else if (candidate.includes(value)) score = 20 + candidate.indexOf(value);

          return { row, score };
        })
        .filter((item) => Number.isFinite(item.score))
        .sort((a, b) => a.score - b.score || a.row.index - b.row.index)
        .slice(0, 5)
        .map((item) => item.row);
    }

    const numericValue = Number.parseFloat(value);
    if (!Number.isFinite(numericValue)) return [];

    const numericRows = this.rows.map((row) => Number.parseFloat(row.us));
    const minimum = Math.min(...numericRows);
    const maximum = Math.max(...numericRows);
    if (numericValue < minimum || numericValue > maximum) return [];

    return [...this.rows]
      .sort((a, b) => {
        const distanceA = Math.abs(Number.parseFloat(a.us) - numericValue);
        const distanceB = Math.abs(Number.parseFloat(b.us) - numericValue);
        return distanceA - distanceB || a.index - b.index;
      })
      .slice(0, 5);
  }

  renderSuggestions(rows) {
    this.currentSuggestions = rows;
    this.activeOptionIndex = -1;
    this.suggestions.replaceChildren();

    rows.forEach((row, index) => {
      const option = document.createElement('li');
      const primary = document.createElement('strong');
      const secondary = document.createElement('span');

      option.id = `${this.suggestions.id}-option-${index}`;
      option.dataset.suggestionIndex = String(index);
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      primary.textContent = this.source === 'uk' ? `UK ${row.uk}` : `US ${row.us}`;
      secondary.textContent =
        this.source === 'uk'
          ? `US ${row.us} · ${row.diameter} mm`
          : `UK ${row.uk} · ${row.diameter} mm`;
      option.append(primary, secondary);
      this.suggestions.append(option);
    });

    this.suggestions.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.input.removeAttribute('aria-activedescendant');
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeSuggestions();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.suggestions.hidden) {
        const value =
          this.source === 'uk' ? this.normalizeUK(this.input.value) : this.normalizeUS(this.input.value);
        const rows = value ? this.findSuggestions(value) : this.rows.slice(0, 5);
        if (rows.length > 0) this.renderSuggestions(rows);
      }

      if (this.currentSuggestions.length === 0) return;
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex =
        (this.activeOptionIndex + direction + this.currentSuggestions.length) % this.currentSuggestions.length;
      this.setActiveOption(nextIndex);
      return;
    }

    if (event.key === 'Enter' && !this.suggestions.hidden && this.activeOptionIndex >= 0) {
      event.preventDefault();
      this.chooseSuggestion(this.activeOptionIndex);
    }
  }

  setActiveOption(index) {
    const options = [...this.suggestions.querySelectorAll('[role="option"]')];
    options.forEach((option, optionIndex) => {
      option.setAttribute('aria-selected', optionIndex === index ? 'true' : 'false');
    });

    this.activeOptionIndex = index;
    const activeOption = options[index];
    if (activeOption) {
      this.input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    }
  }

  chooseSuggestion(index) {
    const row = this.currentSuggestions[index];
    if (!row) return;

    this.input.value = this.source === 'uk' ? row.uk : row.us;
    this.clearButton.hidden = false;
    this.clearError();
    this.closeSuggestions();
    this.updateResult(row, true, true);
  }

  closeSuggestions() {
    this.suggestions.hidden = true;
    this.suggestions.replaceChildren();
    this.currentSuggestions = [];
    this.activeOptionIndex = -1;
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
  }

  updateResult(row, exact, announce) {
    this.resultLabel.textContent = exact ? this.dataset.matchLabel : this.dataset.closestLabel;
    this.resultEmpty.hidden = true;
    this.resultValues.hidden = false;
    this.resultUK.textContent = row.uk;
    this.resultUS.textContent = row.us;
    this.resultDiameter.textContent = row.diameter;
    this.resultCircumference.textContent = row.circumference;
    this.previewSize.textContent = row.uk;

    const diameter = Number.parseFloat(row.diameter);
    const scale = 0.72 + ((diameter - 13.21) / (21.89 - 13.21)) * 0.28;
    this.preview.style.setProperty('--ring-preview-scale', String(Math.min(1, Math.max(0.72, scale))));

    this.tableRows.forEach((tableRow) => tableRow.classList.remove('is-match'));
    row.element.classList.add('is-match');

    if (announce) {
      this.resultStatus.textContent = `UK ${row.uk} matches US ${row.us}. Inside diameter ${row.diameter} millimetres and circumference ${row.circumference} millimetres.`;
    }
  }

  resetResult() {
    this.resultLabel.textContent = this.dataset.closestLabel;
    this.resultEmpty.textContent = this.dataset.emptyResult;
    this.resultEmpty.hidden = false;
    this.resultValues.hidden = true;
    this.previewSize.textContent = '?';
    this.preview.style.setProperty('--ring-preview-scale', '0.78');
    this.resultStatus.textContent = '';
    this.tableRows.forEach((tableRow) => tableRow.classList.remove('is-match'));
  }

  showError() {
    this.input.setAttribute('aria-invalid', 'true');
    this.inputError.textContent = this.inputError.dataset.errorMessage;
    this.inputError.hidden = false;
  }

  clearError() {
    this.input.removeAttribute('aria-invalid');
    this.inputError.hidden = true;
    this.inputError.textContent = '';
  }
}

if (!customElements.get('ring-size-converter')) {
  customElements.define('ring-size-converter', TLJRingSizeConverter);
}
